import type { AnimalKind } from "../game/animals";
import type { Migration, PersistedRecord } from "./store";

export type ControlPreference = "auto" | "joystick" | "keyboard";

export type GameMode = "exploration" | "survival";

export const DEFAULT_GAME_MODE: GameMode = "exploration";

export interface ProfileProgress {
  readonly score: number;
  readonly lastAnimal: AnimalKind | null;
}

export interface ProfileUnlocks {
  readonly animals: readonly AnimalKind[];
}

export interface ProfileSettings {
  readonly audio: boolean;
  readonly controls: ControlPreference;
  readonly gameMode: GameMode;
}

export interface ProfileOnboarding {
  readonly completed: boolean;
}

export interface ProfileSnapshot {
  readonly progress: ProfileProgress;
  readonly unlocks: ProfileUnlocks;
  readonly settings: ProfileSettings;
  readonly onboarding: ProfileOnboarding;
}

const ALL_ANIMALS: readonly AnimalKind[] = ["seal", "penguin", "fish"];
const VALID_ANIMAL_KINDS: ReadonlySet<string> = new Set<AnimalKind>(ALL_ANIMALS);
const VALID_CONTROLS: ReadonlySet<string> = new Set<ControlPreference>([
  "auto",
  "joystick",
  "keyboard",
]);
const VALID_GAME_MODES: ReadonlySet<string> = new Set<GameMode>(["exploration", "survival"]);

export function defaultProfile(): ProfileSnapshot {
  return {
    progress: { score: 0, lastAnimal: null },
    unlocks: { animals: [...ALL_ANIMALS] },
    settings: { audio: true, controls: "auto", gameMode: DEFAULT_GAME_MODE },
    onboarding: { completed: false },
  };
}

function isGameMode(value: unknown): value is GameMode {
  return typeof value === "string" && VALID_GAME_MODES.has(value);
}

function isAnimalKind(value: unknown): value is AnimalKind {
  return typeof value === "string" && VALID_ANIMAL_KINDS.has(value);
}

function isControlPreference(value: unknown): value is ControlPreference {
  return typeof value === "string" && VALID_CONTROLS.has(value);
}

function isProgress(value: unknown): value is ProfileProgress {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.score !== "number" || !Number.isFinite(v.score) || v.score < 0) {
    return false;
  }
  return v.lastAnimal === null || isAnimalKind(v.lastAnimal);
}

function isUnlocks(value: unknown): value is ProfileUnlocks {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.animals)) return false;
  return v.animals.every(isAnimalKind);
}

function isSettings(value: unknown): value is ProfileSettings {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.audio === "boolean" && isControlPreference(v.controls) && isGameMode(v.gameMode);
}

function isOnboarding(value: unknown): value is ProfileOnboarding {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.completed === "boolean";
}

export function isProfileSnapshot(value: unknown): value is ProfileSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    isProgress(v.progress) &&
    isUnlocks(v.unlocks) &&
    isSettings(v.settings) &&
    isOnboarding(v.onboarding)
  );
}

const v1ToV2: Migration = (prev) => {
  if (typeof prev !== "object" || prev === null) return prev;
  const p = prev as Record<string, unknown>;
  return { ...p, onboarding: { completed: true } };
};

const v2ToV3: Migration = (prev) => {
  if (typeof prev !== "object" || prev === null) return prev;
  const p = prev as Record<string, unknown>;
  const settings =
    typeof p.settings === "object" && p.settings !== null
      ? (p.settings as Record<string, unknown>)
      : {};
  return { ...p, settings: { ...settings, gameMode: DEFAULT_GAME_MODE } };
};

export const PROFILE_RECORD: PersistedRecord<ProfileSnapshot> = {
  key: "profile",
  version: 3,
  defaults: defaultProfile,
  validate: isProfileSnapshot,
  migrations: {
    1: v1ToV2,
    2: v2ToV3,
  },
};
